/*global window, console, document*/
import * as React from "react";
import { useState, useEffect, useLayoutEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { TabList, Button } from "@fluentui/react-components";
import CommonTooltip from "../../common/CommonTooltip";
import { useTranslation } from "react-i18next";
import { Delete } from "../../../assets/icons";
import { remove as removeBorder, onChange as onBorderChange } from "../../../state/bordersSlice";
import { remove, updateCell } from "../../../state/structureSlice";
import { onChange as onSpacerChange } from "../../../state/spacerStylesSlice";
import { deselect, hover } from "../../../state/selectionSlice";
import { onSelection, onSelectionByCoordinates, isSpacerSelected } from "../../../helpers/selection";
import { batchGroupBy } from "../../../helpers/batchGroupBy";
import { clearCustomImage, removeShape, setIcon } from "../../../state/cellStylesSlice";
import useStyles from "./styles";
import ContextMenuTab from "./ContextMenuTab";
import HorizontalContextMenuRightSide from "./HorizontalContextMenuRightSide";

const HorizontalContextMenuContent = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selection = useSelector((state) => state.selection);
  const structure = useSelector((state) => state.gridd.present.structure);
  const lastOpenAccordionItem = useSelector((state) => state.app.lastOpenAccordionItem);
  const [isSubMenuSelectedByKeyboard, setIsSubMenuSelectedByKeyboard] = useState(false);
  const [isUsingKeyboard, setIsUsingKeyboard] = useState(false);

  const getInitialTab = () => {
    if (selection.table) return "style";
    if (selection.borders.length > 0) {
      return lastOpenAccordionItem === "structure" ? "structure" : "preset";
    }

    if (isSpacerSelected(selection, structure)) return "spacer";

    if (
      lastOpenAccordionItem &&
      !isSpacerSelected(selection, structure) &&
      !selection.table &&
      selection.borders.length === 0
    ) {
      return lastOpenAccordionItem;
    }

    return "structure";
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  const menuContexts = {
    default: {
      tabs: {
        standard: ["structure", "style", "shape", "image", "icon", "background", "borders", "merge"],
        spacer: ["structure", "background", "spacer"],
      },
      conditions: {
        structure: () => selection.columns.length > 0 || selection.rows.length > 0,
        style: () => !isSpacerSelected(selection, structure),
        spacer: () => isSpacerSelected(selection, structure),
        background: () => true,
      },
    },
    border: {
      tabs: ["structure", "preset"],
      condition: () => selection.borders.length > 0,
    },
    table: {
      tabs: ["structure", "style", "shape", "image", "icon", "background", "borders", "merge"],
      condition: () => selection.table,
    },
  };

  const getApplicableTabs = () => {
    if (selection.table) {
      return menuContexts.table.tabs;
    }
    if (shouldShowBorderMenu()) {
      return menuContexts.border.tabs;
    }

    const spacerSelected = isSpacerSelected(selection, structure);
    const tabKey = spacerSelected ? "spacer" : "standard";

    return menuContexts.default.tabs[tabKey].filter((tab) => {
      const condition = menuContexts.default.conditions[tab];
      return !condition || condition();
    });
  };

  const shouldShowBorderMenu = () => selection.borders.length > 0;
  const hasStructureSelected = () => selection.columns.length > 0 || selection.rows.length > 0;

  const onDeleteBtnClick = () => {
    if (shouldShowBorderMenu()) {
      dispatch(removeBorder(selection.borders[0]));
      dispatch(deselect(selection.borders[0]));
      return;
    }

    if (hasStructureSelected()) {
      batchGroupBy.start();
      for (let i = 0; i < Math.max(selection.columns.length, selection.rows.length); i++) {
        const columnIndex = selection.columns[0];
        const rowIndex = selection.rows[0];

        dispatch(deselect({ column: columnIndex, row: rowIndex }));
        dispatch(remove({ columnIndex, rowIndex }));
        dispatch(onBorderChange({ columnIndexRemoved: columnIndex, rowIndexRemoved: rowIndex }));
        dispatch(onSpacerChange({ columnIndexRemoved: columnIndex, rowIndexRemoved: rowIndex }));
      }
      batchGroupBy.end();
      return;
    }

    try {
      onSelectionByCoordinates(dispatch, updateCell, { selection, structure }, { text: "<p></p>" });
      onSelection(dispatch, removeShape, { selection, structure });
      onSelection(dispatch, clearCustomImage, { selection, structure });
      onSelection(dispatch, setIcon, { selection, structure }, { icon: undefined, iconAlign: undefined });
    } catch (error) {
      console.error("Error during delete operation:", error);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tabs = getApplicableTabs();
      const currentIndex = tabs.indexOf(selectedTab);

      // Set keyboard navigation flag
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        setIsUsingKeyboard(true);
      }

      if (isSubMenuSelectedByKeyboard) {
        if (e.key === "ArrowLeft") {
          setIsSubMenuSelectedByKeyboard(false);
        }
      } else {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          const newIndex =
            e.key === "ArrowUp" ? (currentIndex - 1 + tabs.length) % tabs.length : (currentIndex + 1) % tabs.length;
          setSelectedTab(tabs[newIndex]);
        } else if (e.key === "ArrowRight") {
          setIsSubMenuSelectedByKeyboard(true);
        }
      }
    };

    // Reset keyboard navigation flag on mouse move
    const handleMouseMove = () => {
      setIsUsingKeyboard(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [selectedTab, isSubMenuSelectedByKeyboard]);

  useEffect(() => {
    const tabs = getApplicableTabs();
    if (tabs.includes(selectedTab)) return;

    if (isSpacerSelected(selection, structure) && selectedTab !== "spacer") {
      setSelectedTab("spacer");
      return;
    }

    setSelectedTab(tabs[0]);
  }, [selection, selectedTab, structure]);

  useEffect(() => {
    dispatch(hover({ cell: null }));
  }, [dispatch]);

  // Adjust border context menu to stay within iframe container bounds
  useLayoutEffect(() => {
    const wrapper = document.getElementById("context_menu");
    if (!wrapper) return;
    const container = document.getElementById("container") || document.body;
    const hardcodedOffset = 45; // manual offset as it does not "fit" the container entirely in powerpoint otherwise..
    const contWidth = container.clientWidth - hardcodedOffset;
    const contHeight = container.clientHeight;
    const menuRect = wrapper.getBoundingClientRect();
    let newLeft = menuRect.left - container.getBoundingClientRect().left;
    let newTop = menuRect.top - container.getBoundingClientRect().top;
    // Clamp within container's client area
    if (newLeft + menuRect.width > contWidth) newLeft = contWidth - menuRect.width;
    if (newLeft < 0) newLeft = 0;
    if (newTop + menuRect.height > contHeight) newTop = contHeight - menuRect.height;
    if (newTop < 0) newTop = 0;
    wrapper.style.left = `${newLeft}px`;
    wrapper.style.top = `${newTop}px`;
  }, []);

  return (
    <div
      id="context_menu_parent_div"
      style={{
        pointerEvents: "all",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
        borderRadius: "4px",
        background: "white",
        position: "relative",
        zIndex: 10000,
      }}
    >
      <div
        id="context_menu_grid_div"
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          background: "white",
          borderRadius: "inherit",
          position: "relative",
          zIndex: 10000,
        }}
      >
        <div
          id="context_menu_left_div"
          style={{
            border: isUsingKeyboard && !isSubMenuSelectedByKeyboard ? "2px solid #000000" : "none",
            width: "36px",
            background: "#E4E4E4",
            textAlign: "center",
          }}
        >
          <TabList
            size="small"
            vertical={true}
            selectedValue={selectedTab}
            style={{
              background: "#E4E4E4",
            }}
            className={classes.TabListCustom}
          >
            {getApplicableTabs().map((tab) => (
              <ContextMenuTab
                key={`${tab}_tab`}
                id={`${tab}_tab`}
                value={tab}
                label={
                  tab === "background" && isSpacerSelected(selection, structure)
                    ? "menu.spacer.background.trigger"
                    : `menu.${tab}.trigger`
                }
                onClick={() => setSelectedTab(tab)}
              />
            ))}
          </TabList>

          <CommonTooltip content={t("menu.structure.delete")}>
            <Button
              id="delete_structure_button"
              className={classes.HangingButton}
              appearance="subtle"
              icon={<Delete />}
              iconPosition="before"
              onClick={onDeleteBtnClick}
            />
          </CommonTooltip>
        </div>

        <HorizontalContextMenuRightSide
          selectedTab={selectedTab}
          isBorderMenu={shouldShowBorderMenu()}
          isSubMenuSelectedByKeyboard={isSubMenuSelectedByKeyboard}
          selection={selection}
        />
      </div>
    </div>
  );
};

export default HorizontalContextMenuContent;
